'use client';

import { TRPCReactProvider } from '@/lib/trpc/react';
import { LiveblocksProvider } from '@liveblocks/react';
import { Provider } from 'jotai';
import { SessionProvider } from 'next-auth/react';

import { usePathname } from 'next/navigation';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { type ReactNode, useEffect } from 'react';

import {
  FingerprintJSPro,
  FpjsProvider,
} from '@fingerprintjs/fingerprintjs-pro-react';

import ConfirmationStore from '@/components/Confirmation/ConfirmationStore';
import { Toaster } from '@/components/Sonner';
import { ThemeProvider } from '@/components/theme-provider';
import { TourProvider } from '@/components/tour/TourContext';
import { AuthContextProvider } from '@/context/AuthContext';
import { ModelFallback } from '@/context/ModelFallback';
import { UnriddleContextProvider } from '@/context/UnriddleContext';
import { WindowProvider } from '@/context/WindowContext';
import { Suspense } from 'react';

const fpjsPublicApiKey = process.env.NEXT_PUBLIC_FPJS_PUBLIC_API_KEY;

import { PostHogPageView } from '@/components/PostHogPageView';
import type { Session } from 'next-auth';

interface ProvidersProps {
  children: ReactNode;
  session?: Session | null;
}

const lightOnlyPages = [
  '/pricing',
  '/login',
  '/signup',
  '/',
  '/changelog',
  '/no-emails',
  '/discount-checkout',
  '/faqs',
  '/payment',
  '/summarize',
  '/affiliate',
  '/blog',
  '/homepage',
];

function ProvidersContent({ children, session }: ProvidersProps) {
  const pathname = usePathname();

  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host:
        process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      autocapture: false,
      person_profiles: 'identified_only',
      // Enable debug mode in development
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') posthog.debug();
      },
    });
  }, []);

  // Determine forced theme
  const forcedTheme =
    lightOnlyPages.includes(pathname || '') ||
    (pathname || '').startsWith('/blog') ||
    (pathname || '').startsWith('/authors') ||
    (pathname || '').startsWith('/papers') ||
    (pathname || '').startsWith('/changelog')
      ? 'light'
      : undefined;

  // Determine if it's a landing page
  const isLandingPage =
    pathname === '/' ||
    pathname === '/pricing' ||
    pathname === '/changelog' ||
    (pathname || '').startsWith('/changelog/') ||
    pathname === '/faqs' ||
    pathname === '/login' ||
    pathname === '/checkout' ||
    pathname === '/no-emails' ||
    pathname === '/discount-checkout' ||
    (pathname || '').startsWith('/blog') ||
    (pathname || '').startsWith('/authors') ||
    (pathname || '').startsWith('/papers') ||
    (pathname || '').startsWith('/chat-paper') ||
    pathname === '/404';

  // Special handling for auth pages
  const isAuthPage = pathname === '/auth/error' || pathname === '/auth/email';

  if (isAuthPage) {
    return (
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem
        disableTransitionOnChange
        storageKey="unriddle-theme"
      >
        <SessionProvider session={session}>{children}</SessionProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      disableTransitionOnChange
      storageKey="unriddle-theme"
      forcedTheme={forcedTheme}
    >
      <TRPCReactProvider>
        <FpjsProvider
          loadOptions={{
            apiKey: fpjsPublicApiKey as string,
            endpoint: [
              'https://metrics.anara.com',
              FingerprintJSPro.defaultEndpoint,
            ],
            scriptUrlPattern: [
              'https://metrics.anara.com/web/v<version>/<apiKey>/loader_v<loaderVersion>.js',
              FingerprintJSPro.defaultScriptUrlPattern,
            ],
          }}
        >
          <NuqsAdapter>
            <Provider>
              <PostHogProvider client={posthog}>
                <SessionProvider session={session}>
                  <LiveblocksProvider authEndpoint="/api/liveblocks-auth">
                    <ConfirmationStore.Provider initialValue={null}>
                      <AuthContextProvider isLandingPage={isLandingPage}>
                        <ModelFallback />
                        <UnriddleContextProvider>
                          <WindowProvider>
                            <TourProvider>
                              <Suspense fallback={null}>
                                <PostHogPageView />
                              </Suspense>
                              <Toaster />
                              {children}
                            </TourProvider>
                          </WindowProvider>
                        </UnriddleContextProvider>
                      </AuthContextProvider>
                    </ConfirmationStore.Provider>
                  </LiveblocksProvider>
                </SessionProvider>
              </PostHogProvider>
            </Provider>
          </NuqsAdapter>
        </FpjsProvider>
      </TRPCReactProvider>
    </ThemeProvider>
  );
}

export function Providers({ children, session }: ProvidersProps) {
  return <ProvidersContent session={session}>{children}</ProvidersContent>;
}
